// src/services/analyticsService.ts
import * as Sentry from '@sentry/react';
import { recordClientDiagnostic } from '../../lib/clientDiagnostics';
import {
  ANALYTICS_ENABLED,
  ANALYTICS_PROVIDER,
  POSTHOG_KEY,
  POSTHOG_HOST,
  GA4_MEASUREMENT_ID
} from '../../lib/env';


export type AnalyticsScalar = string | number | boolean | string[] | undefined;
export type AnalyticsEventMetadata = Record<string, AnalyticsScalar>;

const compactRecord = <T extends AnalyticsEventMetadata>(record: T): AnalyticsEventMetadata =>
  Object.fromEntries(Object.entries(record).filter(([, value]) => typeof value !== 'undefined'));

const compactEvent = (event: AnalyticsEvent): AnalyticsEvent => {
  const compacted: AnalyticsEvent = {
    category: event.category,
    action: event.action
  };

  if (typeof event.label !== 'undefined') compacted.label = event.label;
  if (typeof event.value !== 'undefined') compacted.value = event.value;
  if (typeof event.metadata !== 'undefined') compacted.metadata = event.metadata;

  return compacted;
};

export interface AnalyticsEvent {
  category: 'user' | 'product' | 'order' | 'scanner' | 'returns' | 'payment' | 'navigation' | 'socket';
  action: string;
  label?: string;
  value?: number;
  metadata?: AnalyticsEventMetadata;
}

// Window analytics properties are declared globally in src/types/window.d.ts
interface _DeprecatedWindowWithAnalytics extends Window {
  posthog?: {
    init?: (key: string, opts?: Record<string, unknown>) => void;
    identify?: (id: string, traits?: Record<string, unknown>) => void;
    reset?: () => void;
    capture?: (name: string, props?: Record<string, unknown>) => void;
  };
  __posthogBootstrapped?: boolean;
  __posthogQueue?: Array<() => void>;
  __ga4Bootstrapped?: boolean;
  dataLayer?: unknown[];
  gtag?: (...args: unknown[]) => void;
}

/**
 * M5 (hard launch v2): canonical product-analytics events.
 *
 * The growth team relies on these three to build the funnel:
 *   - checkout_start    → user lands on /checkout (or opens drawer)
 *   - checkout_complete → server returned an order id (auth or capture)
 *   - order_delivered   → driver flow marked the run as delivered
 *
 * Naming is fixed because PostHog/GA4 dashboards key off these
 * exact strings; do NOT rename without coordinating with growth.
 */
export type CanonicalAnalyticsEvent =
  | { name: 'checkout_start'; props?: { cartValueCents?: number; itemCount?: number; userId?: string } }
  | { name: 'checkout_complete'; props?: { orderId: string; totalCents?: number; userId?: string } }
  | { name: 'order_delivered'; props?: { orderId: string; driverId?: string; deliveredAt?: string } };

interface AnalyticsDestination {
  identify(userId: string, traits?: Record<string, unknown>): void;
  reset(): void;
  capture(eventName: string, props?: Record<string, unknown>): void;
}

/**
 * Lazy-loaded PostHog + GA4 destinations. We avoid bundling them into
 * the main chunk; the SDK script is injected only when configured.
 */
const createPosthogDestination = (key: string, host: string): AnalyticsDestination => {
  const flush = (fn: () => void) => {
    const win = window;
    if (win.posthog && typeof win.posthog.capture === 'function') {
      fn();
    } else {
      // Queue for after init
      win.__posthogQueue = win.__posthogQueue || [];
      win.__posthogQueue.push(fn);
    }
  };
  if (typeof window !== 'undefined') {
    const win = window;
    if (!win.__posthogBootstrapped) {
      win.__posthogBootstrapped = true;
      const script = document.createElement('script');
      script.src = `${host.replace(/\/$/, '')}/static/array.js`;
      script.async = true;
      script.onload = () => {
        const w = window;
        if (w.posthog && typeof w.posthog.init === 'function') {
          w.posthog.init(key, { api_host: host, autocapture: false, capture_pageview: false });
          const queue: Array<() => void> = w.__posthogQueue || [];
          queue.forEach(fn => {
            try { fn(); } catch { /* noop */ }
          });
          w.__posthogQueue = [];
        }
      };
      document.head.appendChild(script);
    }
  }
  return {
    identify(userId, traits) {
      flush(() => {
        const win = window;
        win.posthog?.identify?.(userId, traits);
      });
    },
    reset() {
      flush(() => {
        const win = window;
        win.posthog?.reset?.();
      });
    },
    capture(name, props) {
      flush(() => {
        const win = window;
        win.posthog?.capture?.(name, props || {});
      });
    }
  };
};

const createGa4Destination = (measurementId: string): AnalyticsDestination => {
  if (typeof window !== 'undefined') {
    const win = window;
    if (!win.__ga4Bootstrapped) {
      win.__ga4Bootstrapped = true;
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
      document.head.appendChild(script);
      win.dataLayer = win.dataLayer || [];
      win.gtag = function gtag() {
        win.dataLayer?.push(arguments);
      };
      win.gtag('js', new Date());
      win.gtag('config', measurementId, { send_page_view: false } as Record<string, unknown>);
    }
  }
  return {
    identify(userId, traits) {
      try { window.gtag?.('set', { user_id: userId, ...(traits || {}) }); } catch { /* noop */ }
    },
    reset() {
      try { window.gtag?.('set', { user_id: undefined }); } catch { /* noop */ }
    },
    capture(name, props) {
      try { window.gtag?.('event', name, props || {}); } catch { /* noop */ }
    }
  };
};

const createNoopDestination = (): AnalyticsDestination => ({
  identify() { /* noop */ },
  reset() { /* noop */ },
  capture() { /* noop */ }
});

const buildDestination = (): AnalyticsDestination => {
  if (typeof window === 'undefined') return createNoopDestination();
  if (ANALYTICS_PROVIDER === 'posthog' && POSTHOG_KEY) {
    return createPosthogDestination(POSTHOG_KEY, POSTHOG_HOST);
  }
  if (ANALYTICS_PROVIDER === 'ga4' && GA4_MEASUREMENT_ID) {
    return createGa4Destination(GA4_MEASUREMENT_ID);
  }
  return createNoopDestination();
};

const COOKIE_CONSENT_STORAGE_KEY = 'ninpo-cookie-consent.v1';

const readAnalyticsConsent = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY) === 'accepted';
  } catch {
    return false;
  }
};

class AnalyticsService {
  private enabled: boolean = false;
  private destination: AnalyticsDestination = createNoopDestination();
  private consentGranted: boolean = false;

  constructor() {
    this.consentGranted = readAnalyticsConsent();
    this.enabled = ANALYTICS_ENABLED && this.consentGranted;
    this.destination = this.enabled ? buildDestination() : createNoopDestination();
    if (typeof window !== 'undefined') {
      window.addEventListener('cookie-consent', ((event: Event) => {
        const detail = (event as CustomEvent<{ choice?: string }>).detail;
        const choice = detail?.choice;
        if (choice === 'accepted') {
          this.setConsentGranted(true);
        } else if (choice === 'rejected') {
          this.setConsentGranted(false);
        }
      }) as EventListener);
    }
  }

  setConsentGranted(granted: boolean): void {
    this.consentGranted = granted;
    this.enabled = ANALYTICS_ENABLED && granted;
    this.destination = this.enabled ? buildDestination() : createNoopDestination();
  }

  /**
   * M5 (hard launch v2): emit a canonical product-analytics event to
   * the configured destination (PostHog/GA4) AND record it as a Sentry
   * breadcrumb. The set of allowed event names is closed in the type
   * so a typo is a compile error rather than a silent dashboard miss.
   */
  trackCanonical(event: CanonicalAnalyticsEvent): void {
    if (!this.enabled) {
      Sentry.addBreadcrumb({ category: 'analytics', message: event.name, level: 'debug', data: event.props as Record<string, unknown> | undefined });
      return;
    }
    try {
      this.destination.capture(event.name, event.props as Record<string, unknown> | undefined);
    } catch (err) {
      recordClientDiagnostic('[Analytics] destination capture failed', { error: String(err) }, 'warning');
    }
    Sentry.addBreadcrumb({
      category: 'analytics',
      message: event.name,
      level: 'info',
      data: event.props as Record<string, unknown> | undefined
    });
  }

  /**
   * Track a custom event
   */
  trackEvent(event: AnalyticsEvent): void {
    if (!this.enabled) {
      Sentry.addBreadcrumb({ category: event.category, message: event.action, level: 'debug', data: event.metadata });
      return;
    }

    // Send to Sentry as breadcrumb
    Sentry.addBreadcrumb({
      category: event.category,
      message: event.action,
      level: 'info',
      data: {
        label: event.label,
        value: event.value,
        ...event.metadata
      }
    });
  }

  /**
   * Track page view
   */
  trackPageView(page: string, userId?: string): void {
    this.trackEvent(compactEvent({
      category: 'navigation',
      action: 'page_view',
      label: page,
      metadata: compactRecord({ userId, timestamp: new Date().toISOString() })
    }));
  }

  /**
   * Track user action
   */
  trackUserAction(action: string, details?: AnalyticsEventMetadata): void {
    this.trackEvent(compactEvent({
      category: 'user',
      action,
      ...(details ? { metadata: details } : {})
    }));
  }

  /**
   * Track product interaction
   */
  trackProductInteraction(action: 'view' | 'add_to_cart' | 'remove_from_cart', productId: string, productName?: string): void {
    this.trackEvent(compactEvent({
      category: 'product',
      action,
      ...(productName ? { label: productName } : {}),
      metadata: { productId }
    }));
  }

  /**
   * Track order events
   */
  trackOrder(action: 'initiated' | 'completed' | 'failed', orderId?: string, total?: number): void {
    this.trackEvent(compactEvent({
      category: 'order',
      action: `order_${action}`,
      ...(orderId ? { label: orderId } : {}),
      ...(typeof total === 'number' ? { value: total } : {}),
      metadata: compactRecord({ orderId, total })
    }));
  }

  /**
   * Track scanner usage
   */
  trackScanner(action: 'opened' | 'scanned' | 'success' | 'error', details?: AnalyticsEventMetadata): void {
    this.trackEvent(compactEvent({
      category: 'scanner',
      action: `scanner_${action}`,
      ...(details ? { metadata: details } : {})
    }));
  }

  /**
   * Track bottle return events
   */
  trackReturn(action: 'started' | 'container_scanned' | 'completed' | 'cancelled', containerCount?: number, credit?: number): void {
    this.trackEvent(compactEvent({
      category: 'returns',
      action: `return_${action}`,
      ...(typeof containerCount === 'number' ? { value: containerCount } : {}),
      metadata: compactRecord({ containerCount, credit })
    }));
  }

  /**
   * Track payment events
   */
  trackPayment(method: 'credits' | 'stripe', status: 'initiated' | 'success' | 'failed', amount?: number, error?: string): void {
    this.trackEvent(compactEvent({
      category: 'payment',
      action: `payment_${status}`,
      label: method,
      ...(typeof amount === 'number' ? { value: amount } : {}),
      metadata: compactRecord({ method, amount, error })
    }));
  }

  /**
   * Set user context for Sentry
   */
  setUser(userId: string, email?: string, tier?: string): void {
    Sentry.setUser({
      id: userId,
      email,
      tier
    });
    try {
      this.destination.identify(userId, { email, tier });
    } catch (err) {
      recordClientDiagnostic('[Analytics] destination identify failed', { error: String(err) }, 'warning');
    }
  }

  /**
   * Clear user context
   */
  clearUser(): void {
    Sentry.setUser(null);
    try {
      this.destination.reset();
    } catch (err) {
      recordClientDiagnostic('[Analytics] destination reset failed', { error: String(err) }, 'warning');
    }
  }

  /**
   * Track error manually
   */
  trackError(error: Error, context?: AnalyticsEventMetadata): void {
    Sentry.captureException(error, {
      extra: context
    });
  }

}

// Export singleton instance
export const analytics = new AnalyticsService();
